import { Badge, Box, Flex, Icon, IconButton, Input } from '@chakra-ui/react'
import { ListFilter } from 'lucide-react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'

import { useDevicesI18n } from '../i18n'

interface DevicesSearchBarProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  debounceMs?: number
  showFilterButton?: boolean
  filtersActive?: boolean
  activeFilterCount?: number
  onToggleFilters?: () => void
}

export const DevicesSearchBar = memo<DevicesSearchBarProps>(
  ({
    value,
    onChange,
    placeholder: placeholderProp,
    debounceMs = 300,
    showFilterButton = false,
    filtersActive = false,
    activeFilterCount = 0,
    onToggleFilters,
  }) => {
    const { text } = useDevicesI18n()
    const placeholder =
      placeholderProp ??
      text(
        'devices.list.searchPlaceholder',
        'Search by name, MAC, extension...',
      )
    const inputRef = useRef<HTMLInputElement>(null)
    const [localValue, setLocalValue] = useState(value)
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const hadFocusRef = useRef(false)

    const handleFocus = useCallback(() => {
      hadFocusRef.current = true
    }, [])

    const handleBlur = useCallback(() => {
      hadFocusRef.current = false
    }, [])

    useEffect(() => {
      if (hadFocusRef.current && inputRef.current) {
        requestAnimationFrame(() => {
          inputRef.current?.focus()
        })
      }
    })

    useEffect(() => {
      setLocalValue(value)
    }, [value])

    const handleChange = useCallback(
      (newValue: string) => {
        setLocalValue(newValue)

        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
          onChange(newValue)
        }, debounceMs)
      },
      [onChange, debounceMs],
    )

    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
        }
      }
    }, [])

    return (
      <Flex align="center" gap={0}>
        <Box w="300px" position="relative">
          <Icon
            boxSize={4}
            color="fg.muted"
            position="absolute"
            left={3}
            top="50%"
            transform="translateY(-50%)"
            zIndex={1}
            pointerEvents="none"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </Icon>
          <Input
            ref={inputRef}
            placeholder={placeholder}
            value={localValue}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            size="sm"
            pl={10}
            bg="bg.subtle"
            borderColor="border.default"
            borderRightRadius={showFilterButton ? 0 : undefined}
            _placeholder={{ color: 'fg.subtle' }}
            _hover={{ borderColor: 'border.emphasized' }}
            _focus={{
              borderColor: 'brand.solid',
              boxShadow: '0 0 0 1px var(--chakra-colors-brand-solid)',
            }}
          />
        </Box>
        {showFilterButton && (
          <Box position="relative">
            <IconButton
              aria-label={text(
                'devices.filters.toggleFilters',
                'Toggle filters',
              )}
              size="sm"
              variant={filtersActive ? 'solid' : 'outline'}
              colorPalette={filtersActive ? 'brand' : undefined}
              onClick={onToggleFilters}
              borderLeftRadius={0}
              borderLeftWidth={0}
            >
              <ListFilter size={16} />
            </IconButton>
            {activeFilterCount > 0 && (
              <Badge
                position="absolute"
                top="-1"
                right="-1"
                size="xs"
                colorPalette="info"
                borderRadius="full"
                minW={4}
                h={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="2xs"
              >
                {activeFilterCount}
              </Badge>
            )}
          </Box>
        )}
      </Flex>
    )
  },
)

DevicesSearchBar.displayName = 'DevicesSearchBar'
